
import { api, type ApiResponse, type RequestOptions } from './base';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import { compressImageFile } from '@/shared/lib/compress-image';
import type { TaskAttachment } from './sprints';

export type ChatKind = 'group' | 'topic';
export type ChatNotifyMode = 'all' | 'mentions' | 'none';

export interface ChatView {
  id: string;
  kind: ChatKind;
  supportGroupId: string | null;
  title: string;
  description: string | null;
  avatarUrl: string | null;
  unread: number;
  notifyMode: ChatNotifyMode;
  lastMessage: { id: string; authorId: string; body: string; kind: 'user' | 'system'; createdAt: string } | null;
}

export interface ChatHistoryParams {
  beforeId?: string;
  afterId?: string;
  around?: string;
  limit?: number;
}

export interface ChatHistoryPage {
  items: ChatMessage[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
}

export interface ChatSystemEvents {
  members: boolean;
  sprints: boolean;
  tickets: boolean;
}

export interface ChatDetail extends ChatView {
  systemEvents: ChatSystemEvents;
  pinnedCount: number;
  canManage: boolean;
}

export interface ChatMention {
  id: string;
  displayName: string;
}

export interface ChatSystemInfo {
  event: 'member_joined' | 'member_left' | 'sprint_started' | 'sprint_closed' | 'ticket_assigned' | 'pinned';
  userId?: string;
  userName?: string | null;
  sprintId?: string;
  ticketId?: string;
  messageId?: string;
  href?: string;
}

export interface ChatMessage {
  id: string;
  chatId: string;
  authorId: string;
  author: { id: string; displayName: string; avatarUrl?: string | null } | null;
  body: string;
  kind: 'user' | 'system';
  system: ChatSystemInfo | null;
  replyToMessageId: string | null;
  replyToAttachmentId: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  pinned: boolean;
  createdAt: string;
  attachments: TaskAttachment[];
  mentions: ChatMention[];
}

export interface ChatReader {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  lastReadAt: string;
}

export interface ChatMemberView {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
  isLead: boolean;
  online?: boolean;
  lastSeenAt?: string | null;
}

export type ChatSharedKind = 'media' | 'files' | 'links';

export interface ChatSharedCounts {
  media: number;
  files: number;
  links: number;
}

type ChatSharedAuthor = { id: string; displayName: string; avatarUrl?: string | null } | null;

export interface ChatSharedFile extends TaskAttachment {
  messageId: string;
  author: ChatSharedAuthor;
}

export interface ChatSharedLink {
  url: string;
  messageId: string;
  createdAt: string;
  author: ChatSharedAuthor;
}

export interface ChatSharedPage<T> {
  items: T[];
  hasMore: boolean;
  counts?: ChatSharedCounts;
}

export interface RefPreview {
  kind: 'ticket' | 'task';
  id: string;
  label: string;
  title: string | null;
  status: string | null;
  href: string;
}

export interface RefHit {
  kind: 'ticket' | 'task';
  id: string;
  key: string;
  title: string;
  subtitle: string | null;
  status: string;
  statusLabel: string | null;
  open: boolean;
  updatedAt: string;
  href: string;
}

export interface TopicChat {
  id: string;
  title: string;
  description: string | null;
  avatarUrl: string | null;
  archivedAt: string | null;
  createdAt: string;
  systemEvents: ChatSystemEvents;
  members: { id: string; displayName: string }[];
  groups: { id: string; name: string }[];
  messagesCount: number;
}

function multipart<T>(url: string, file: File, onProgress?: (percent: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    Promise.all([getValidToken(), compressImageFile(file)])
      .then(([token, compressed]) => {
        const form = new FormData();
        form.append('file', compressed);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${config.apiUrl}${url}`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText) as { data?: T; error?: { message?: string } };
            if (xhr.status >= 200 && xhr.status < 300 && json.data) resolve(json.data);
            else reject(new Error(json.error?.message ?? `Ошибка загрузки (${xhr.status})`));
          } catch {
            reject(new Error(`Ошибка загрузки (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error('Сетевая ошибка при загрузке файла'));
        xhr.send(form);
      })
      .catch(reject);
  });
}

export const chatsApi = {
  searchRefs: (q: string, limit = 8) =>
    api
      .get<{ tickets: RefHit[]; tasks: RefHit[] }>(`/api/v1/chats/search?q=${encodeURIComponent(q)}&limit=${limit}`)
      .then((r) => r.data),
  list: () => api.get<ChatView[]>('/api/v1/chats').then((r) => r.data),
  unread: () => api.get<{ unread: number }>('/api/v1/chats/unread').then((r) => r.data.unread),
  preview: (ref: string) =>
    api.get<RefPreview>(`/api/v1/chats/preview?ref=${encodeURIComponent(ref)}`, undefined, { toast: false }).then((r) => r.data),
  detail: (id: string, opts?: RequestOptions) => api.get<ChatDetail>(`/api/v1/chats/${id}`, undefined, opts).then((r) => r.data),
  updateSettings: (id: string, input: { title?: string | null; description?: string | null; systemEvents?: Partial<ChatSystemEvents> }) =>
    api.patch<ChatDetail>(`/api/v1/chats/${id}`, input).then((r) => r.data),
  members: (id: string, opts?: RequestOptions) => api.get<ChatMemberView[]>(`/api/v1/chats/${id}/members`, undefined, opts).then((r) => r.data),
  shared: <K extends ChatSharedKind>(id: string, kind: K, beforeId?: string) => {
    const qs = new URLSearchParams({ kind });
    if (beforeId) qs.set('beforeId', beforeId);
    return api
      .get<(K extends 'links' ? ChatSharedLink : ChatSharedFile)[]>(`/api/v1/chats/${id}/shared?${qs}`)
      .then((r) => {
        const extra = r as ApiResponse<unknown> & { hasMore?: boolean; counts?: ChatSharedCounts };
        return { items: r.data, hasMore: Boolean(extra.hasMore), counts: extra.counts } as ChatSharedPage<K extends 'links' ? ChatSharedLink : ChatSharedFile>;
      });
  },
  reads: (id: string, opts?: RequestOptions) => api.get<ChatReader[]>(`/api/v1/chats/${id}/reads`, undefined, opts).then((r) => r.data),
  messages: (id: string, params: ChatHistoryParams = {}): Promise<ChatHistoryPage> => {
    const q = new URLSearchParams();
    for (const key of ['beforeId', 'afterId', 'around'] as const) if (params[key]) q.set(key, params[key]);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return api.get<ChatMessage[]>(`/api/v1/chats/${id}/messages${qs ? `?${qs}` : ''}`).then((r) => {
      const flags = r as ApiResponse<ChatMessage[]> & { hasMore?: boolean; hasMoreBefore?: boolean; hasMoreAfter?: boolean };
      return {
        items: r.data,
        hasMoreBefore: Boolean(flags.hasMoreBefore ?? flags.hasMore),
        hasMoreAfter: Boolean(flags.hasMoreAfter),
      };
    });
  },
  message: (id: string, messageId: string) => api.get<ChatMessage>(`/api/v1/chats/${id}/messages/${messageId}`).then((r) => r.data),
  send: (
    id: string,
    body: { body: string; attachmentIds?: string[]; replyToMessageId?: string | null; replyToAttachmentId?: string | null },
  ) => api.post<ChatMessage>(`/api/v1/chats/${id}/messages`, body).then((r) => r.data),
  edit: (id: string, messageId: string, body: string) =>
    api.patch<ChatMessage>(`/api/v1/chats/${id}/messages/${messageId}`, { body }).then((r) => r.data),
  remove: (id: string, messageId: string) =>
    api.delete<ChatMessage>(`/api/v1/chats/${id}/messages/${messageId}`).then((r) => r.data),
  removeMany: (id: string, messageIds: string[]) =>
    api.post<ChatMessage[]>(`/api/v1/chats/${id}/messages/delete`, { messageIds }).then((r) => r.data),
  pins: (id: string, opts?: RequestOptions) => api.get<ChatMessage[]>(`/api/v1/chats/${id}/pins`, undefined, opts).then((r) => r.data),
  pin: (id: string, messageId: string) => api.put<ChatMessage>(`/api/v1/chats/${id}/pins/${messageId}`, {}).then((r) => r.data),
  unpin: (id: string, messageId: string) => api.delete<ChatMessage>(`/api/v1/chats/${id}/pins/${messageId}`).then((r) => r.data),
  unpinAll: (id: string) => api.delete<{ ok: true }>(`/api/v1/chats/${id}/pins`).then((r) => r.data),
  markRead: (id: string) => api.post<{ ok: true }>(`/api/v1/chats/${id}/read`, {}).then((r) => r.data),
  setNotifyMode: (id: string, mode: ChatNotifyMode) =>
    api.put<{ mode: ChatNotifyMode }>(`/api/v1/chats/${id}/notifications`, { mode }).then((r) => r.data),
  mentionable: (id: string) =>
    api.get<(ChatMention & { kind: 'user' | 'all' })[]>(`/api/v1/chats/${id}/mentionable`).then((r) => r.data),
  uploadAttachment: (id: string, file: File, onProgress?: (percent: number) => void): Promise<TaskAttachment> =>
    multipart<TaskAttachment>(`/api/v1/chats/${id}/attachments`, file, onProgress),
  uploadAvatar: (id: string, file: File): Promise<{ avatarUrl: string }> => multipart<{ avatarUrl: string }>(`/api/v1/chats/${id}/avatar`, file),
  removeAvatar: (id: string) => api.delete<{ avatarUrl: null }>(`/api/v1/chats/${id}/avatar`).then((r) => r.data),
};

export const adminChatsApi = {
  list: () => api.get<TopicChat[]>('/api/v1/admin/chats').then((r) => r.data),
  create: (input: { title: string; description?: string | null }) => api.post<TopicChat>('/api/v1/admin/chats', input).then((r) => r.data),
  update: (id: string, input: { title?: string; description?: string | null; archived?: boolean }) =>
    api.patch<TopicChat>(`/api/v1/admin/chats/${id}`, input).then((r) => r.data),
  remove: (id: string) => api.delete<unknown>(`/api/v1/admin/chats/${id}`),
  setMembers: (id: string, input: { userIds: string[]; groupIds: string[] }) =>
    api.put<TopicChat>(`/api/v1/admin/chats/${id}/members`, input).then((r) => r.data),
};
